import * as React from 'react';
import { useState } from 'react';

import { Check } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { appConfig, Environment } from '@app/config/appConfig';
import { usePermissions } from '@app/permissions/hooks/usePermissions';
import { Permission } from '@app/permissions/permissions';
import { Role } from '@app/utils/protected-routes.utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shared/components/ui';
import { Burger } from '@shared/icons/Burger';
import { cn } from '@shared/utils';

import { urls } from '../urls';

interface HamburgerItem {
  key: string;
  label: string;
  href?: string;
  onClick?: () => void;
  selected?: boolean;
  separator?: boolean;
  children?: HamburgerItem[];
}

export const AppHamburgerMenu = () => {
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const { hasPermission, hasMinimumRole } = usePermissions();

  const location = useLocation();
  const currentPath = location.pathname;

  const handleCloseMenu = () => setIsHamburgerOpen(false);

  const hamburgerItems: HamburgerItem[] = [
    {
      key: 'results',
      label: 'Results',
      href: urls.results.path,
      selected: currentPath === urls.results.path,
      separator: true,
    },
    ...(appConfig.environment !== Environment.PROD && hasPermission(Permission.REPORTS_VIEW)
      ? [
          {
            key: 'reports',
            label: 'Reports',
            href: urls.reports.path,
            selected: currentPath === urls.reports.path,
          },
        ]
      : []),
    ...(hasPermission(Permission.APPLICATIONS_VIEW)
      ? [
          {
            key: 'applications',
            label: 'Applications',
            href: urls.applications.path,
            selected: currentPath === urls.applications.path,
          },
        ]
      : []),
    ...(appConfig.environment !== Environment.PROD && hasPermission(Permission.INTEGRATIONS_VIEW)
      ? [
          {
            key: 'integrations',
            label: 'Integrations',
            selected: currentPath.startsWith(urls.integrations.path),
            children: [
              {
                key: 'integrations-overview',
                label: 'Overview',
                href: urls.integrations.path,
                selected: currentPath === urls.integrations.path,
              },
              {
                key: 'integrations-manage',
                label: 'Manage',
                href: urls.manage.path,
                selected: currentPath === urls.manage.path,
              },
            ],
          },
        ]
      : []),
    ...(hasMinimumRole(Role.ADMIN)
      ? [
          {
            key: 'user-management',
            label: 'User Management',
            href: urls.userManagement.path,
            selected: currentPath === urls.userManagement.path,
          },
        ]
      : []),
    ...(hasPermission(Permission.AUDIT_VIEW_ALL) || hasPermission(Permission.AUDIT_VIEW_TENANT)
      ? [
          {
            key: 'audit-log',
            label: 'Audit Log',
            href: urls.auditLog.path,
            selected: currentPath === urls.auditLog.path,
          },
        ]
      : []),
    ...(hasPermission(Permission.API_KEYS_VIEW)
      ? [
          {
            key: 'api-keys',
            label: 'API Keys',
            href: urls.manageApiKeys.path,
            selected: currentPath === urls.manageApiKeys.path,
          },
        ]
      : []),
    ...(hasPermission(Permission.TOKEN_LEDGER_VIEW)
      ? [
          {
            key: 'token-ledger',
            label: 'Tokens',
            href: urls.tokenLedger.path,
            selected: currentPath === urls.tokenLedger.path,
          },
        ]
      : []),
  ];

  return (
    <div className="relative shrink-0 size-16 hover:bg-accent hover:text-accent-foreground" data-name="menu-button">
      <div
        aria-hidden="true"
        className="absolute border-r border-solid border-border bottom-0 left-0 pointer-events-none -right-px top-0"
      />
      <DropdownMenu open={isHamburgerOpen} onOpenChange={setIsHamburgerOpen}>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              'flex items-center justify-center w-16 h-16 border-border ml-auto cursor-pointer',
              'hover:bg-accent',
              isHamburgerOpen && 'bg-accent text-foreground',
            )}
            data-testid="app-hamburger-menu-trigger"
          >
            <Burger className="-translate-y-px" />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-[200px] rounded-md border border-muted-border bg-popover">
          {hamburgerItems.map((item) => {
            // Render Parent with Sub-menu
            if (item.children && item.children.length > 0) {
              return (
                <React.Fragment key={item.key}>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      className={cn(
                        'flex items-center gap-2 cursor-pointer',
                        item.selected && 'text-primary font-medium',
                      )}
                    >
                      {item.label}
                    </DropdownMenuSubTrigger>

                    <DropdownMenuPortal>
                      <DropdownMenuSubContent className="w-[180px] rounded-md border border-muted-border bg-popover shadow-md ml-1">
                        {item.children.map((child) => (
                          <React.Fragment key={child.key}>
                            {child.href ? (
                              <DropdownMenuItem asChild>
                                <Link
                                  to={child.href}
                                  onClick={handleCloseMenu}
                                  className="flex items-center justify-between gap-2 cursor-pointer w-full"
                                >
                                  <span>{child.label}</span>
                                  {child.selected && <Check className="h-4 w-4 text-primary shrink-0" />}
                                </Link>
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => {
                                  child.onClick?.();
                                  handleCloseMenu();
                                }}
                                className="flex items-center justify-between gap-2 cursor-pointer"
                              >
                                <span>{child.label}</span>
                                {child.selected && <Check className="h-4 w-4 text-primary shrink-0" />}
                              </DropdownMenuItem>
                            )}
                          </React.Fragment>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                  {item.separator && <DropdownMenuSeparator />}
                </React.Fragment>
              );
            }

            // Render Standard Item
            return (
              <React.Fragment key={item.key}>
                {item.href ? (
                  <DropdownMenuItem asChild>
                    <Link
                      to={item.href}
                      onClick={handleCloseMenu}
                      className="flex items-center justify-between gap-2 cursor-pointer w-full"
                    >
                      <span>{item.label}</span>
                      {item.selected && <Check className="h-4 w-4 text-primary shrink-0" />}
                    </Link>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => {
                      item.onClick?.();
                      handleCloseMenu();
                    }}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                  >
                    <span>{item.label}</span>
                    {item.selected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </DropdownMenuItem>
                )}
                {item.separator && <DropdownMenuSeparator />}
              </React.Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
